/* eslint-disable no-param-reassign */
/* eslint-disable no-restricted-syntax */
import { HIST_ST_QRY_SB, HIST_ST_QRY_TAB } from 'Hooks';
import { Buffer } from 'buffer';
import { uniqueId } from 'lodash/fp';
import * as R from 'ramda';
import React from 'react';
import {
  SITE_DTL_BROWSER_PATH,
  TASK_ASSIGNMENT_BROWSER_PATH
} from 'routes/constants';

export function keyValueList(data, key) {
  const lov = (x) => ({ label: x, value: x });
  const label = (x) => x[key];
  return R.is(Array, data)
    ? R.compose(
        R.sortBy(R.prop('label')),
        R.map(lov),
        R.uniq(),
        R.map(label)
      )(data)
    : [];
}

export const getUniqLov = (data, valueCol, labelCol) => {
  const label = labelCol || valueCol;
  const result = R.is(Array, data)
    ? R.compose(
        R.sortBy(R.prop('label')),
        R.uniq(),
        R.map((x) => ({ value: R.prop(valueCol)(x), label: R.prop(label)(x) }))
      )(data)
    : [];
  return result;
};

const isPrimary = (task) => task.isPrimaryYn === 'Yes';
const isNonPrimaryAndRequired = (task) =>
  task.isPrimaryYn === 'No' && task.isRequiredYn === 'Yes';
const isNonPrimaryNonRequired = (task) =>
  task.isPrimaryYn === 'No' && task.isRequiredYn === 'No';
const sortByTaskName = (taskList) =>
  R.sortBy(R.compose(R.toLower, R.prop('taskLabel')))(taskList); // changing to lowecase as uppcase letter take precedence.

export function sortTaskList(response) {
  const primaryTasks =
    R.compose(sortByTaskName, R.filter(isPrimary))(response) || [];
  const nonPrimaryRequiredTasks =
    R.compose(sortByTaskName, R.filter(isNonPrimaryAndRequired))(response) ||
    [];
  const nonPrimaryNonRequiredTasks =
    R.compose(sortByTaskName, R.filter(isNonPrimaryNonRequired))(response) ||
    [];
  return [
    ...primaryTasks,
    ...nonPrimaryRequiredTasks,
    ...nonPrimaryNonRequiredTasks
  ];
}

export function encodeFiltersToUrl(appliedFilters) {
  const sanitizeFilterState = Object.fromEntries(
    Object.entries(appliedFilters).filter(([, v]) => v !== '')
  );
  const searchParams = Buffer.from(
    new URLSearchParams(sanitizeFilterState).toString()
  ).toString('base64');
  return searchParams;
}

export function decodeBase64Url(query = '') {
  const params = Buffer.from(query, 'base64').toString('utf-8');
  return new URLSearchParams(params) || {};
}

export const getWebsiteEnv = () => {
  const host = window.location.hostname;
  let websiteEnv = '';
  if (host.search('unit-') >= 0) {
    websiteEnv = 'UNIT';
  } else if (host.search('uat-') >= 0) {
    websiteEnv = 'UAT';
  } else if (host.search('ci-') >= 0 || host.search('localhost') >= 0) {
    websiteEnv = 'CI';
  } else if (host.search('test-') >= 0) {
    websiteEnv = 'TEST';
  } else {
    websiteEnv = 'PROD';
  }
  return websiteEnv;
};

export const getAppOrigin = () => {
  const origin = window.location.origin;
  const env_app_orgin = process.env.REACT_APP_ORIGIN.slice(
    0,
    process.env.REACT_APP_ORIGIN.length - 1
  );
  return origin.search('localhost') >= 0 ? env_app_orgin : origin;
};

export function checkIsDevEnv() {
  const devEnvs = ['CI', 'UNIT', 'TEST'];
  const currentEnv = getWebsiteEnv();
  return devEnvs.indexOf(currentEnv) > -1;
}

export const getGAId = () => {
  const GACode = (type) => {
    try {
      return JSON.parse(process.env.REACT_APP_GA)[type];
    } catch (e) {
      return '';
    }
  };
  const websiteEnv = window.location.hostname;
  let gaTrackingId = '';
  if (websiteEnv.search('unit-') >= 0) {
    gaTrackingId = GACode('UNIT');
  } else if (websiteEnv.search('uat-') >= 0) {
    gaTrackingId = GACode('UAT');
  } else if (
    websiteEnv.search('ci-') >= 0 ||
    websiteEnv.search('localhost') >= 0
  ) {
    gaTrackingId = GACode('CI');
  } else if (websiteEnv.search('test-') >= 0) {
    gaTrackingId = GACode('TEST');
  } else {
    gaTrackingId = GACode('PROD');
  }
  return gaTrackingId;
};

export function moveItemToTop(list, item) {
  const idx = list?.indexOf(item);
  if (idx > -1) {
    return R.pipe(R.move(idx, 0))(list);
  }
  return list;
}
export const generateKey = (pre, post, index) => `${pre}_${index}_${post}`;

export const removeParams = (route) => route?.path?.split(':')[0];

export function getRouteSubPaths(allRoutes, mainPath) {
  const routeToFind = allRoutes.find((route) => route.path === mainPath);
  if (routeToFind && Array.isArray(routeToFind.subRoutes)) {
    return routeToFind.subRoutes.map(removeParams);
  }
  return [];
}

export const getRandomkey = () => {
  const array = new Uint32Array(1);
  return crypto?.getRandomValues(array);
};
export function updateRoutesAvailability(routes, callback) {
  return routes.map((route) => {
    if (route.condition) {
      route.isAvailable = callback(route.condition);
    } else {
      route.isAvailable = true;
    }
    if (route.subRoutes) {
      route.subRoutes = updateRoutesAvailability(route.subRoutes, callback);
    }
    return route;
  });
}

export function flattenRoutes(appRoutes) {
  let flatRoutes = [];
  if (Array.isArray(appRoutes)) {
    appRoutes.forEach((r) => {
      flatRoutes.push(r);
      if (r.subRoutes) {
        flatRoutes = [...flatRoutes, ...r.subRoutes];
      }
    });
  }
  return flatRoutes;
}

export function filterUnavailableRoutes(routes) {
  const filteredRoutes = [];

  routes.forEach((route) => {
    const obj = { ...route };
    if (obj.isAvailable) {
      if (obj.subRoutes) {
        obj.subRoutes = filterUnavailableRoutes(obj.subRoutes);
      }
      filteredRoutes.push(obj);
    }
  });

  return filteredRoutes;
}

export const renderProps = (props) => {
  if (typeof props === 'function') {
    return <>{props()}</>;
  }
  return <>{props}</>;
};

export function getUserTypeFromStorage() {
  const currentUserTypeElement = localStorage
    .getItem('currentUserType')
    ?.toString();
  const decodedValue =
    currentUserTypeElement &&
    Buffer.from(currentUserTypeElement, 'base64').toString('utf-8');
  const decodedValueArray =
    decodedValue?.split('=').filter((item) => item) || [];
  return decodedValueArray.length >= 1 ? decodedValueArray[0] : '';
}

export function getUserTypeWithCtepIdFromStorage() {
  const currentUserTypeElement = localStorage
    .getItem('currentUserType')
    ?.toString();
  const decodedValue =
    currentUserTypeElement &&
    Buffer.from(currentUserTypeElement, 'base64').toString('utf-8');
  const decodedValueArray =
    decodedValue?.split('=').filter((item) => item) || [];
  return decodedValueArray;
}

export function setUserTypeToStorage(userType) {
  localStorage.setItem(
    'currentUserType',
    Buffer.from(userType).toString('base64')
  );
}

export function getItemValueFromStorage(itemName, deleteAfterRead = 'Y') {
  const currentItemValue = localStorage.getItem(itemName)?.toString();
  const decodedValue =
    currentItemValue &&
    Buffer.from(currentItemValue, 'base64').toString('utf-8');
  if (deleteAfterRead === 'Y') {
    localStorage.removeItem(itemName); //delete the item after reading.
  }
  return decodedValue;
}

export function setItemValueToStorage(itemName, itemValue) {
  itemValue &&
    localStorage.setItem(itemName, Buffer.from(itemValue).toString('base64'));
}

function AddValue({ params }) {
  const NOT_AVAILABLE = '';

  if (params && typeof params === 'object') {
    return (
      <>
        {params.map((param) => (
          <p key={uniqueId('parent-')} className="d-block">
            {param}
          </p>
        ))}
      </>
    );
  }

  return <>{renderProps(params) || NOT_AVAILABLE}</>;
}

export function List({
  label,
  value,
  classNameLabel = '',
  classNameValue = '',
  labelCol = '4'
}) {
  return (
    <ul className="row list-nostyle mb-1">
      <li
        key={label}
        className={`col-${labelCol} mr-3 align-right color-dark-blue ${classNameLabel}`}
      >
        {label}
      </li>
      <li key={value} className={`col-${12 - labelCol} ${classNameValue}`}>
        <AddValue params={value} />
      </li>
    </ul>
  );
}

// If the useNavigate is provided, it will open TAB Browser in place, otherwise it will open in new browser tab.
export function viewTasksByFilterState(
  filterState,
  navigate,
  queryValue,
  viewType = 'ALL'
) {
  const filter = encodeFiltersToUrl({
    viewType,
    ...filterState
  });
  window.sessionStorage.setItem(HIST_ST_QRY_TAB, filter);
  const url = `${TASK_ASSIGNMENT_BROWSER_PATH}?${HIST_ST_QRY_TAB}=${
    queryValue || filter
  }`;

  if (navigate) {
    navigate(url);
  } else {
    window.open(url);
  }
}

// If the useNavigate is provided, it will open Site DTL Browser in place, otherwise it will open in new browser tab.
export function viewDtlsByFilterState(filterState, navigate, viewType = 'ALL') {
  const filter = encodeFiltersToUrl({
    viewType,
    ...filterState
  });

  window.sessionStorage.setItem(HIST_ST_QRY_SB, filter);
  const url = `${SITE_DTL_BROWSER_PATH}?${HIST_ST_QRY_SB}=${filter}`;

  if (navigate) {
    navigate(url);
  } else {
    window.open(url);
  }
}

export const appendFilter = (field, values) => (filter) =>
  values && values !== 'All'
    ? R.append(
        {
          field,
          operation: 'in',
          value: values.split(',')
        },
        filter
      )
    : filter;

// construct request body for any filter api
export const getDashboardFilterRequestBody = (
  siteFilterValue,
  ciFilterValue
) => {
  const filter = R.compose(
    appendFilter('siteCtepId', siteFilterValue),
    appendFilter('ciCtepId', ciFilterValue)
  )([]);
  return filter.length > 0 ? { filter } : {};
};
